import { useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { IoIosArrowDropdown } from "react-icons/io";
import ExcelJS from 'exceljs';
import {useAuth} from '../../Auth/AuthContext'; 
import styles from './ExportPDF.module.css'; 
import swal from 'sweetalert2'; 
import logos from './logos.png'; 

const ExportXLSX = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const {getToken} = useAuth(); 
    const token = getToken(); 

    const fetchReporteData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/reporte-instituciones-pasos`,{
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            }); 
            if (!response.ok) {
                throw new Error('Error al obtener los datos del reporte');
            }
            return await response.json();
        } catch (error) {
            console.error('Error:', error);
            throw error;
        }
    };

    const fetchTotalPasos = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/total-pasos`,{
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            }); 
            if (!response.ok) {
                throw new Error('Error al obtener el número total de pasos');
            }
            const result = await response.json();
            return result.total_pasos;
        } catch (error) {
            console.error('Error:', error);
            throw error;
        }
    };

    const fetchUsuariosDetalle = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/usuarios-detalle`, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            }); 
            if (!response.ok) {
                throw new Error('Error al obtener los datos detallados de usuarios');
            }
            const result = await response.json();
            
            return {
                usuarios: result.usuarios || result, // Por compatibilidad
                tests: result.tests || [],
                etapasSecciones: result.etapasSecciones || []
            };
        } catch (error) {
            console.error('Error:', error);
            throw error;
        }
    };

    const imageToBuffer = async (imageSrc) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                canvas.toBlob((blob) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsArrayBuffer(blob);
                }, 'image/png');
            };
            img.onerror = reject;
            img.src = imageSrc;
        });
    };

    // Función para generar el archivo POR pasos
    const generateResumenXLSX = async (data, totalPasos) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Reporte - Progresos por pasos.');

        // Configurar propiedades del workbook
        workbook.creator = 'Tu Carrera';
        workbook.created = new Date();
        workbook.modified = new Date();

        // AÑADIR IMAGEN/LOGO
        try {
            const imageBuffer = await imageToBuffer(logos);
            const imageId = workbook.addImage({
                buffer: imageBuffer,
                extension: 'png',
            });

            worksheet.addImage(imageId, {
                tl: { col: 0, row: 0 },
                ext: { width: 250, height: 80 }
            });
        } catch (imageError) {
            console.warn('No se pudo añadir la imagen:', imageError);
        }

        // TÍTULO PRINCIPAL - Ajustado para incluir la nueva columna
        worksheet.mergeCells('B1:' + String.fromCharCode(68 + totalPasos) + '1');
        const titleCell = worksheet.getCell('B1');
        titleCell.value = 'Tu Carrera';
        titleCell.style = {
            font: { 
                bold: true, 
                size: 18, 
            },
            alignment: { 
                horizontal: 'center', 
                vertical: 'middle' 
            },
        };
        worksheet.getRow(1).height = 80;

        worksheet.addRow([]);
        worksheet.addRow([]);
        worksheet.addRow([]);

        // ENCABEZADOS - Incluir nueva columna
        const headers = [
            'Bachillerato/Estado', 
            'Cantidad de Usuarios',
            'Usuarios con Todos los Pasos Completados'
        ];
        for (let i = 1; i <= totalPasos; i++) {
            headers.push(`Paso ${i}`);
        }

        const headerRow = worksheet.addRow(headers);
        
        // Estilos para los encabezados
        headerRow.eachCell((cell, colNumber) => {
            let fillColor = 'FFD9D9D9'; 
            
            switch(colNumber) {
                case 1:
                    fillColor = 'FFB4C6E7';
                    break;
                case 2:
                    fillColor = 'FFFFCC99';
                    break;
                case 3:
                    fillColor = 'FFC5E0B4'; // Color verde claro para la nueva columna
                    break;
                default:
                    const pasoIndex = colNumber - 4; // Ajustado por la nueva columna
                    fillColor = pasoIndex % 2 === 0 ? 'FFE2EFDA' : 'FFFFF2CC';
                    break;
            }

            cell.style = {
                font: { 
                    bold: true, 
                    color: { argb: 'FF000000' },
                    size: 12
                },
                fill: {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: fillColor }
                },
                alignment: { 
                    horizontal: 'center', 
                    vertical: 'middle' 
                },
            };
        });
        headerRow.height = 25;

        // DATOS - Incluir la nueva columna
        data.forEach((row, index) => {
            const rowData = [
                row.institucion || 'Otros estados',
                row.cantidad_usuarios || 0,
                row.usuarios_todos_pasos_completados || 0
            ];

            for (let i = 1; i <= totalPasos; i++) {
                rowData.push(row[`paso_${i}`] || 0);
            }

            const dataRow = worksheet.addRow(rowData);
            
            dataRow.eachCell((cell, colNumber) => {
                const isEvenRow = index % 2 === 0;
                const fillColor = isEvenRow ? 'FFFFFFFF' : 'FFF8F9FA';

                cell.style = {
                    alignment: { 
                        horizontal: colNumber === 1 ? 'left' : 'center',
                        vertical: 'middle' 
                    },
                    fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: fillColor }
                    },
                    font: {
                        size: 11
                    }
                };
            });
            dataRow.height = 20;
        });

        // FILA DE TOTALES - Incluir la nueva columna
        const totalesData = [
            'TOTALES',
            data.reduce((sum, row) => sum + (row.cantidad_usuarios || 0), 0),
            data.reduce((sum, row) => sum + (row.usuarios_todos_pasos_completados || 0), 0)
        ];

        for (let i = 1; i <= totalPasos; i++) {
            totalesData.push(data.reduce((sum, row) => sum + (row[`paso_${i}`] || 0), 0));
        }

        const totalesRow = worksheet.addRow(totalesData);
        
        totalesRow.eachCell((cell, colNumber) => {
            cell.style = {
                font: { 
                    bold: true, 
                    size: 12
                },
                fill: {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE2EFDA' }
                },
                alignment: { 
                    horizontal: 'center', 
                    vertical: 'middle' 
                },
            };
        });
        totalesRow.height = 25;

        // CONFIGURAR ANCHOS DE COLUMNAS - Ajustado para la nueva columna
        worksheet.getColumn(1).width = 35;
        worksheet.getColumn(2).width = 20;
        worksheet.getColumn(3).width = 25; // Nueva columna

        for (let i = 4; i <= 3 + totalPasos; i++) { // Ajustado índices
            worksheet.getColumn(i).width = 12;
        }

        // AÑADIR FECHA DE GENERACIÓN
        worksheet.addRow([]);
        worksheet.addRow(['', `Generado el: ${new Date().toLocaleDateString('es-ES', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })}`]);
        
        const fechaCell = worksheet.getCell(worksheet.rowCount, 2);
        fechaCell.style = {
            font: { 
                italic: true, 
                size: 10,
                color: { argb: 'FF666666' }
            },
            alignment: { horizontal: 'left' }
        };

        return workbook;
    };

    // Archivo detallado de usuarios y sus avances en SECCIONES. 
    const generateUsuariosDetalleXLSX = async (usuariosData, testsData, etapasSeccionesData = [], seccionesEnUso = []) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Reporte - Progreso por secciones y tests');

        // Configurar propiedades del workbook
        workbook.creator = 'Tu Carrera';
        workbook.created = new Date();
        workbook.modified = new Date();

        worksheet.addRow([]);

        // ENCABEZADOS
        const headers = [
            'Apellido Paterno',
            'Apellido Materno', 
            'Nombres',
            'CURP',
            'Fecha Nacimiento',
            'Estado',
            'Municipio',
            'Bachillerato',
            'Matrícula',
            'Institución',
            'Teléfono',
            'Correo',
            'Correo Tutor',
        ];

        // Crear estructura para organizar elementos por paso y orden
        const elementosPorPaso = {};
        const etapaNames = {};
        const seccionYaAgregada = new Set(); // Para evitar duplicados de secciones compartidas
        
        // Procesar información de etapas y secciones
        etapasSeccionesData.forEach(item => {
            const paso = item.etapa;
            const orden = item.orden;
            
            if (!elementosPorPaso[paso]) {
                elementosPorPaso[paso] = [];
            }
            
            // Para secciones: solo agregar una vez (la primera aparición por orden)
            if (item.seccion && !seccionYaAgregada.has(item.seccion)) {
                elementosPorPaso[paso].push({
                    tipo: 'seccion',
                    numero: item.seccion,
                    orden: orden,
                    etapa: paso
                });
                seccionYaAgregada.add(item.seccion);
            }
            
            // Para tests: agregar normalmente
            if (item.test) {
                elementosPorPaso[paso].push({
                    tipo: 'test',
                    numero: item.test,
                    orden: orden,
                    etapa: paso
                });
            }
            
            // Guardar nombres de etapas
            etapaNames[paso] = item.etapa_nombre || `Paso ${paso}`;
        });

        // Ordenar elementos dentro de cada paso por orden
        Object.keys(elementosPorPaso).forEach(paso => {
            elementosPorPaso[paso].sort((a, b) => a.orden - b.orden);
        });

        // Crear headers ordenados por paso y luego por orden
        const elementosOrdenados = [];
        const pasosOrdenados = Object.keys(elementosPorPaso).sort((a, b) => parseInt(a) - parseInt(b));
        
        pasosOrdenados.forEach(paso => {
            elementosPorPaso[paso].forEach(elemento => {
                if (elemento.tipo === 'seccion') {
                    headers.push(`Paso ${paso} - Sección ${elemento.numero} (%)`);
                    elementosOrdenados.push({
                        tipo: 'seccion',
                        numero: elemento.numero,
                        etapa: paso,
                        header: `Paso ${paso} - Sección ${elemento.numero} (%)`
                    });
                } else if (elemento.tipo === 'test') {
                    headers.push(`Paso ${paso} - Test ${elemento.numero} (%)`);
                    elementosOrdenados.push({
                        tipo: 'test',
                        numero: elemento.numero,
                        etapa: paso,
                        header: `Paso ${paso} - Test ${elemento.numero} (%)`
                    });
                }
            });
        });

        const headerRow = worksheet.addRow(headers);
        
        // Definir colores para cada etapa/paso
        const etapaColors = {
            1: 'FFE2EFDA', // Verde claro
            2: 'FFFFF2CC', // Amarillo claro  
            3: 'FFFFE6CC', // Naranja claro
            4: 'FFE6E6FA', // Lavanda
            5: 'FFF0F8FF', // Azul muy claro
            6: 'FFFFEFD5', // Beige claro
            7: 'FFE6F3FF', // Azul pálido
            8: 'FFFFE0E6', // Rosa muy claro
        };
        
        headerRow.eachCell((cell, colNumber) => {
            let fillColor = 'FFD9D9D9'; // Color por defecto
            
            // Información personal (columnas 1-11)
            if (colNumber <= 13) {
                fillColor = 'FFB4C6E7'; // Azul para info personal
            } 
            // Secciones y Tests (ordenados por paso)
            else {
                const elementoIndex = colNumber - 14; // 0-based
                if (elementosOrdenados[elementoIndex]) {
                    const etapa = elementosOrdenados[elementoIndex].etapa;
                    if (etapa && etapaColors[etapa]) {
                        fillColor = etapaColors[etapa];
                    } else {
                        fillColor = 'FFDDDDDD'; // Gris para elementos sin etapa
                    }
                }
            }

            cell.style = {
                font: { 
                    bold: true, 
                    color: { argb: 'FF000000' },
                    size: 13
                },
                fill: {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: fillColor }
                },
                alignment: { 
                    horizontal: 'center', 
                    vertical: 'middle' 
                },
            };
        });
        headerRow.height = 25;

        // DATOS
        usuariosData.forEach((usuario, index) => {
            const rowData = [
                usuario.us_appa || '',
                usuario.us_apma || '',
                usuario.us_nombre || '',
                usuario.usp_curp || '',
                usuario.usp_fecha_nacimiento || '',
                usuario.usp_estado || '',
                usuario.usp_municipio || '',
                usuario.usp_bachillerato || '',
                usuario.usp_matricula || '',
                usuario.usp_institucion || '',
                usuario.usp_telefono || '',
                usuario.us_correo || '',
                usuario.usp_correo_tutor || '',
            ];

            // Agregar progreso siguiendo el orden de elementos
            elementosOrdenados.forEach(elemento => {
                if (elemento.tipo === 'seccion') {
                    const progreso = usuario[`seccion_${elemento.numero}_progreso`] || 0;
                    rowData.push(`${progreso}%`);
                } else if (elemento.tipo === 'test') {
                    const progreso = usuario[`test_${elemento.numero}_progreso`] || 0;
                    rowData.push(`${progreso}%`);
                }
            });

            const dataRow = worksheet.addRow(rowData);
            
            dataRow.eachCell((cell, colNumber) => {
                const isEvenRow = index % 2 === 0;
                const fillColor = isEvenRow ? 'FFFFFFFF' : 'FFF8F9FA';

                cell.style = {
                    alignment: { 
                        horizontal: 'center',
                        vertical: 'middle' 
                    },
                    fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: fillColor }
                    },
                    font: {
                        size: 10
                    }
                };
            });
            dataRow.height = 20;
        });

        // CONFIGURAR ANCHOS DE COLUMNAS
        const columnWidths = [25, 15, 15, 15, 25, 15, 25, 15, 25, 15, 20];
        columnWidths.forEach((width, index) => {
            worksheet.getColumn(index + 1).width = width;
        });

        // Anchos para columnas de elementos (secciones y tests)
        for (let i = 12; i <= 11 + elementosOrdenados.length; i++) {
            worksheet.getColumn(i).width = 20;
        }

        // LEYENDA DE PASOS/ETAPAS
        if (etapasSeccionesData.length > 0) {
            worksheet.addRow([]);
            worksheet.addRow(['LEYENDA DE PASOS:']);
            
            const leyendaCell = worksheet.getCell(worksheet.rowCount, 1);
            leyendaCell.style = {
                font: { 
                    bold: true, 
                    size: 12,
                    color: { argb: 'FF000000' }
                }
            };

            // Mostrar información por paso
            pasosOrdenados.forEach(paso => {
                const etapaNombre = etapaNames[paso] || `Paso ${paso}`;
                const elementos = elementosPorPaso[paso];
                
                let contenido = `Paso ${paso}: ${etapaNombre}`;
                
                // Mostrar elementos en orden
                const elementosTexto = elementos.map(el => {
                    if (el.tipo === 'seccion') {
                        return `S${el.numero}`;
                    } else {
                        return `T${el.numero}`;
                    }
                }).join(', ');
                
                if (elementosTexto) {
                    contenido += ` | Elementos: ${elementosTexto}`;
                }
                
                const row = worksheet.addRow([contenido]);
                
                const cell = row.getCell(1);
                cell.style = {
                    font: { 
                        size: 10,
                        color: { argb: 'FF000000' }
                    },
                    fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: etapaColors[paso] || 'FFD9D9D9' }
                    },
                    alignment: { horizontal: 'left' }
                };
            });
            
            // Nota explicativa
            worksheet.addRow([]);
            const notaRow = worksheet.addRow(['NOTA: S = Sección, T = Test. Los elementos se muestran en el orden que aparecen dentro de cada paso.']);
            const notaCell = notaRow.getCell(1);
            notaCell.style = {
                font: { 
                    italic: true,
                    size: 9,
                    color: { argb: 'FF666666' }
                },
                alignment: { horizontal: 'left' }
            };
        }

        // AÑADIR FECHA DE GENERACIÓN
        worksheet.addRow([]);
        worksheet.addRow(['', `Generado el: ${new Date().toLocaleDateString('es-ES', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })}`]);
        
        const fechaCell = worksheet.getCell(worksheet.rowCount, 2);
        fechaCell.style = {
            font: { 
                italic: true, 
                size: 10,
                color: { argb: 'FF666666' }
            },
            alignment: { horizontal: 'left' }
        };

        return workbook;
    };

    const generateXLSX = async () => {
        setIsLoading(true);
        setError('');

        try {
            const [data, totalPasos, usuariosDetalleResponse] = await Promise.all([
                fetchReporteData(),
                fetchTotalPasos(),
                fetchUsuariosDetalle()
            ]);

            // Extraer usuarios, tests, etapasSecciones Y seccionesEnUso de la respuesta
            const usuariosData = usuariosDetalleResponse.usuarios;
            const testsData = usuariosDetalleResponse.tests;
            const etapasSeccionesData = usuariosDetalleResponse.etapasSecciones;
            const seccionesEnUso = usuariosDetalleResponse.seccionesEnUso;
            
            if (!data || data.length === 0) {
                swal.fire({
                    title: 'Error', 
                    text:'No hay datos para exportar', 
                    icon: 'error', 
                    confirmButtonColor: '#242b57',
                    customClass: { popup: 'popupbonito' }
                });
                throw new Error('No hay datos disponibles para exportar'); 
            }

            const resumenWorkbook = await generateResumenXLSX(data, totalPasos);
            const resumenBuffer = await resumenWorkbook.xlsx.writeBuffer();
            const resumenBlob = new Blob([resumenBuffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            const resumenUrl = window.URL.createObjectURL(resumenBlob);
            const resumenLink = document.createElement('a');
            resumenLink.href = resumenUrl;
            resumenLink.download = `resumen_por_bachillerato_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(resumenLink);
            resumenLink.click();
            window.URL.revokeObjectURL(resumenUrl);
            document.body.removeChild(resumenLink);

            if (usuariosData && usuariosData.length > 0) {
                const usuariosWorkbook = await generateUsuariosDetalleXLSX(
                    usuariosData, 
                    testsData, 
                    etapasSeccionesData,
                    seccionesEnUso // NUEVO PARÁMETRO
                );
                const usuariosBuffer = await usuariosWorkbook.xlsx.writeBuffer();
                const usuariosBlob = new Blob([usuariosBuffer], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                
                // Descargar archivo de usuarios 
                setTimeout(() => {
                    const usuariosUrl = window.URL.createObjectURL(usuariosBlob);
                    const usuariosLink = document.createElement('a');
                    usuariosLink.href = usuariosUrl;
                    usuariosLink.download = `detalle_usuarios_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(usuariosLink);
                    usuariosLink.click();
                    window.URL.revokeObjectURL(usuariosUrl);
                    document.body.removeChild(usuariosLink);
                }, 1000);
            }

            swal.fire({
                title: '¡Éxito!', 
                text: 'Los reportes se han generado y descargado correctamente', 
                icon: 'success', 
                confirmButtonText: 'Aceptar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });

        } catch (error) {
            console.error('Error al generar los archivos:', error);
            setError(error.message || 'Error al generar los archivos Excel');
            
            swal.fire({
                title: 'Error', 
                text: `Error al generar los archivos: ${error.message}`, 
                icon: 'error', 
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <button 
                onClick={generateXLSX} 
                disabled={isLoading}
                className={styles.botonExport}
            >
                {isLoading ? (
                    <>
                        <FaSpinner className={styles.spinner} />
                        Generando reportes...
                    </>
                ) : (
                    <>
                        <IoIosArrowDropdown />
                        Descargar Reportes
                    </>
                )}
            </button>
        </div>
    );
};

export default ExportXLSX;